import { type ReactNode } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'

/**
 * Единый паттерн анимации для всех интерактивных элементов
 */
export const UNIFIED_ANIMATION = {
  tap: { scale: 0.98 },
  transition: { type: 'spring', stiffness: 400, damping: 25 },
} as const

export interface SelectionOptionProps {
  /**
   * Выбран ли элемент
   */
  selected: boolean
  /**
   * Обработчик клика
   */
  onClick: () => void
  /**
   * Основной текст
   */
  label: string
  /**
   * Дополнительный текст
   */
  description?: string
  /**
   * Иконка слева
   */
  icon?: ReactNode
  /**
   * Контент справа (например, стрелка для drill-down)
   */
  rightContent?: ReactNode
  /**
   * Заблокирован ли элемент
   */
  disabled?: boolean
  /**
   * Размер элемента
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * UI компонент: Кнопка выбора
 * 
 * Унифицированный компонент для выбора опций в Bottom Sheet и формах.
 * Используется во всех местах где нужен выбор из списка опций.
 * 
 * @example
 * ```tsx
 * <SelectionOption
 *   selected={weight === 50}
 *   onClick={() => setWeight(50)}
 *   label="50 г"
 *   description="1 250 ₽"
 * />
 * ```
 */
export function SelectionOption({
  selected,
  onClick,
  label,
  description,
  icon,
  rightContent,
  disabled = false,
  size = 'md',
}: SelectionOptionProps) {
  const { hapticFeedback } = useTelegram()

  const handleClick = () => {
    if (disabled) return
    hapticFeedback?.selectionChanged()
    onClick()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'w-full rounded-2xl border-2 transition-colors',
        'flex items-center gap-3 text-left',
        'active:scale-[0.98] transition-transform duration-100',
        // Размеры
        {
          'p-3': size === 'sm',
          'p-4': size === 'md',
          'p-5': size === 'lg',
        },
        // Состояния
        {
          'border-primary bg-primary/5 dark:bg-primary/10': selected,
          'border-silk dark:border-dark-border bg-white dark:bg-dark-card': !selected,
          'hover:border-primary/50 dark:hover:border-primary/50': !selected && !disabled,
          'opacity-50 cursor-not-allowed': disabled,
        }
      )}
    >
      {/* Радио индикатор */}
      <div className={cn(
        'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
        selected ? 'border-primary bg-primary' : 'border-gray-300 dark:border-gray-600'
      )}>
        {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      </div>
      
      {/* Иконка */}
      {icon && (
        <div className={cn(
          'flex-shrink-0',
          selected ? 'text-primary' : 'text-gray-400'
        )}>
          {icon}
        </div>
      )}
      
      {/* Текст */}
      <div className="flex-1 min-w-0">
        <div className={cn(
          'font-medium',
          selected ? 'text-primary dark:text-primary-light' : 'text-ink dark:text-white'
        )}>
          {label}
        </div>
        {description && (
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {description}
          </div>
        )}
      </div>
      
      {/* Контент справа */}
      {rightContent && (
        <div className="flex-shrink-0">
          {rightContent}
        </div>
      )}
    </button>
  )
}

export interface SelectionListProps {
  /**
   * Заголовок списка
   */
  title?: string
  /**
   * Дочерние элементы (SelectionOption)
   */
  children: ReactNode
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * UI компонент: Список выбора
 * 
 * Контейнер для группы SelectionOption.
 * 
 * @example
 * ```tsx
 * <SelectionList title="Выберите вес">
 *   <SelectionOption selected={w === 10} onClick={() => setW(10)} label="10 г" />
 *   <SelectionOption selected={w === 50} onClick={() => setW(50)} label="50 г" />
 *   <SelectionOption selected={w === 100} onClick={() => setW(100)} label="100 г" />
 * </SelectionList>
 * ```
 */
export function SelectionList({ title, children, className }: SelectionListProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {title && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {title}
        </label>
      )}
      <div className="space-y-2">
        {children}
      </div>
    </div>
  )
}

export interface SelectionGridProps {
  /**
   * Дочерние элементы
   */
  children: ReactNode
  /**
   * Количество колонок
   * @default 3
   */
  columns?: 2 | 3 | 4
  /**
   * Дополнительные классы
   */
  className?: string
}

/**
 * UI компонент: Сетка выбора
 * 
 * Для компактного отображения опций в сетке.
 */
export function SelectionGrid({ children, columns = 3, className }: SelectionGridProps) {
  return (
    <div className={cn(
      'grid gap-2',
      {
        'grid-cols-2': columns === 2,
        'grid-cols-3': columns === 3,
        'grid-cols-4': columns === 4,
      },
      className
    )}>
      {children}
    </div>
  )
}

export interface SelectionChipProps {
  /**
   * Выбран ли чип
   */
  selected: boolean
  /**
   * Обработчик клика
   */
  onClick: () => void
  /**
   * Текст
   */
  label: string
  /**
   * Дополнительный текст (под основным)
   */
  sublabel?: string
  /**
   * Иконка
   */
  icon?: ReactNode
  /**
   * Заблокирован ли
   */
  disabled?: boolean
}

/**
 * UI компонент: Чип выбора
 * 
 * Компактная кнопка выбора для сеток.
 */
export function SelectionChip({
  selected,
  onClick,
  label,
  sublabel,
  icon,
  disabled = false,
}: SelectionChipProps) {
  const { hapticFeedback } = useTelegram()

  const handleClick = () => {
    if (disabled) return
    hapticFeedback?.selectionChanged()
    onClick()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'p-3 rounded-xl border-2 transition-colors',
        'flex flex-col items-center gap-1 text-center',
        'active:scale-[0.98] transition-transform duration-100',
        {
          'border-primary bg-primary/5 dark:bg-primary/10': selected,
          'border-silk dark:border-dark-border bg-white dark:bg-dark-card hover:border-primary/50': !selected && !disabled,
          'opacity-50 cursor-not-allowed': disabled,
        }
      )}
    >
      {icon && (
        <div className={cn(
          'mb-1',
          selected ? 'text-primary' : 'text-gray-400'
        )}>
          {icon}
        </div>
      )}
      <span className={cn(
        'text-sm font-medium',
        selected ? 'text-primary dark:text-primary-light' : 'text-ink dark:text-white'
      )}>
        {label}
      </span>
      {sublabel && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {sublabel}
        </span>
      )}
    </button>
  )
}






